import React, { useState, useEffect } from 'react';
import SFCustomTable from '../../component/custom/SFCustomTable';
import { Button } from 'react-md';
import AssignmentDetailModal from '../AssignmentMatrix/Modal/AssignmentDetailModal';
import ApiRequestService from '../../component/ApiRequestService';
import { MdEdit } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { IconButton, Tooltip } from '@mui/material';
import SFConfirmAlert from '../../component/custom/SFConfirmAlert';
import { NotificationManager } from 'react-notifications';
import { MdContentCopy } from "react-icons/md";
import DuplicateTutorial from '../DuplicateTutorial';
import { useTutorialSync, tutorialSyncColumn, TutorialUnsyncedDialog, ReceivedTutorialActionsCell } from './TutorialSync';


const Tutorials = ({tutorialKey, updateTutorialCount}) => {

    const [tutorials, setTutorials] = useState([]);
    const [showConfirmDelete, setShowConfirmDelete] = useState(false);
    const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

    const tutorialSync = useTutorialSync();

    // Rebuilt every render, not held in state: the Sync cells read the sync
    // status, and a column list frozen on the first render never sees it.
    const columns = [
        {
            name: <div className='sf-data-table-header'>ID</div>,
            selector: row => row.quiz_id,
            sortable: true,
            width: '300px',
        },
        {
            name: <div className='sf-data-table-header'>Tutorial Name</div>,
            selector: row => row.quiz_name,
            sortable: true,
        },
        tutorialSyncColumn(tutorialSync),
        {
            name: <div className='sf-data-table-header'>Actions</div>,
            sortable: false,
            width: '200px',
            cell: (row, rowIndex) => {
                // Received from another university - only its owner edits it.
                if (row.is_synced) {
                    return <ReceivedTutorialActionsCell onView={() => getQuizDetail(row, true)} />;
                }
                return (
                    <div>
                        <Tooltip title="Edit" arrow placement="top">
                            <IconButton onClick={() => {
                                getQuizDetail(row);
                            }} >
                                <MdEdit color='#3d5e9c' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Duplicate" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedTutorial(row);
                                setShowDuplicateDialog(true);
                            }} >
                                <MdContentCopy color='#3d5e9c' />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow placement="top">
                            <IconButton onClick={() => {
                                setSelectedTutorial(row);
                                setShowConfirmDelete(true);
                            }} >
                                <FiTrash2 color='#bb0000' />
                            </IconButton>
                        </Tooltip>
                    </div>
                )
            },
            sortable: false,
        },
    ].filter(column => tutorialSync.syncAccess || column.id !== 'sync');
    const [showTutorialDialog, setShowTutorialDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedTutorial, setSelectedTutorial] = useState(null);
    // Set while a received tutorial is open, so the dialog only shows it.
    const [viewOnly, setViewOnly] = useState(false);

    useEffect(() => {
        getAllTutorials();
        tutorialSync.loadSyncStatus();
    }, [])

    const getAllTutorials = () => {
        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/getTutorials',
            data: {
                tutorial_key: tutorialKey,
            }
        }).then(res => {
            if (res && res.data && res.data.data) {
                setTutorials(res.data.data);
                if (updateTutorialCount) updateTutorialCount(res.data.data.length);
            } else {
                setTutorials([]);
                if (updateTutorialCount) updateTutorialCount(0);
            }
            // A duplicate of a tutorial gets its own status row to show.
            tutorialSync.reloadSyncStatus();
        }).finally(() => {
            setLoading(false);
        })
    }

    const deleteTutorial = (quizID) => {
        setLoading(true);
        ApiRequestService({
            method: 'post',
            url: '/softDeleteQuiz',
            data: {
                quiz_id: quizID,
            }
        }).then(res => {
            console.debug("res", res);
            if (res && res.data) {
                if (res.data.message !== "") {
                    NotificationManager.error(res.data.message, "Unable to delete", 4000);
                    return;
                }
                let newTutorials = [...tutorials];
                let index = newTutorials.findIndex(tut => tut.quiz_id === selectedTutorial.quiz_id);
                if (index > -1) {
                    newTutorials.splice(index, 1);
                    setTutorials(newTutorials);
                    if (updateTutorialCount) updateTutorialCount(newTutorials.length);
                }
                tutorialSync.reloadSyncStatus();
                setShowConfirmDelete(false);
                setSelectedTutorial(null);
                
            }
        }).finally(() => {
            setLoading(false);
        })
    }

    const getQuizDetail = (assign, readOnly = false) => {
        let payload = {
            course_id: 0,
            section_id: 0,
            quiz_id: assign.quiz_id,
        }
        ApiRequestService({
            method: 'post',
            url: '/getQuizById',
            data: payload
        }).then(res => {
            if (res.data.data !== null && res.data.data !== undefined && res.data.data.quiz_id) {
                setSelectedTutorial(res.data.data);
                setViewOnly(readOnly);
                setShowTutorialDialog(true);
            }
        })
    }

    return (
        <div className='mt-2'>
            <div style={{ position:'absolute', right: '2rem', zIndex: 9, }} className='mx-4'>
                <Button primary raised onClick={() => {
                    setShowTutorialDialog(true);
                }}>ADD A NEW TUTORIAL</Button>
            </div>
            <div className='mt-2'>
                <SFCustomTable
                    loading={loading}
                    columns={columns}
                    selectableRows={false}
                    data={tutorials}
                    topPagination={false}
                    bottomPagination={true}
                    defaultRowsPerPage={20}
                    showSearch={true}
                    searchByFields={["quiz_name"]}
                />
            </div>

            {showTutorialDialog ?
                <AssignmentDetailModal
                    isTutorial={true}
                    readOnly={viewOnly}
                    showModal={showTutorialDialog}
                    assignment={selectedTutorial}
                    handleClose={() => {
                        setShowTutorialDialog(false);
                        setViewOnly(false);
                    }}
                    onSaveAssignment={(data) => {
                        let updated = [...tutorials];
                        let index = updated.findIndex(t => t.quiz_id === data.quiz_id);
                        if (index > -1) {
                            updated[index] = data;
                        } else {
                            updated.push(data);
                        }
                        setTutorials(updated);
                        if (updateTutorialCount) updateTutorialCount(updated.length);
                        // A save of a synced tutorial queues a push.
                        tutorialSync.reloadSyncStatus();
                    }}
                    course_id={null}
                    handleResetPreview={null}
                    onAfterPreviewClick={() => { }}
                    tutorialKey={tutorialKey}
                />
                : null}

            <DuplicateTutorial
                    open={showDuplicateDialog}
                    quizID={selectedTutorial?.quiz_id}
                    onComplete={() => {
                        setShowDuplicateDialog(false);
                        setSelectedTutorial(null);
                        getAllTutorials();
                    }}
                    onCancel={() => {
                        setShowDuplicateDialog(false);
                        setSelectedTutorial(null);
                    }}
                />

            <SFConfirmAlert
                open={showConfirmDelete}
                title={"Delete Tutorial"}
                description={`Are you sure you want to delete "${selectedTutorial?.quiz_name}"?`}
                onConfirm={() => {
                    if (selectedTutorial && selectedTutorial.quiz_id) {
                        deleteTutorial(selectedTutorial.quiz_id);
                    }
                }}
                loading={loading}
                onCancel={() => {
                    setShowConfirmDelete(false);
                    setSelectedTutorial(null);
                }}
            />

            <TutorialUnsyncedDialog tutorialSync={tutorialSync} />
        </div>
    );
}

export default Tutorials;
